import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, MapPin, Calendar, Package, Loader2 } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { DataTable, Column } from '../components/common/DataTable';
import { StatusBadge } from '../components/common/StatusBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { PaginationControls } from '../components/common/PaginationControls';
import { useKeycloak } from '../auth/keycloakProvider';
import { getDocuments, DocumentDto } from '../api-clients';
import { ECMR, ECMRStatus } from '../types';
import { useToast } from '../hooks/use-toast';
import { getErrorToast } from '../lib/error-utils';

// Helper function to map DocumentDto to ECMR
function mapDocumentDtoToECMR(document: DocumentDto): ECMR {
  // Map DocumentStatus to ECMRStatus
  const statusMap: Record<string, ECMRStatus> = {
    'Draft': 'draft',
    'Pending': 'pending',
    'InTransit': 'in_transit',
    'Delivered': 'delivered',
    'Signed': 'delivered', // Signed is treated as delivered
    'Cancelled': 'cancelled',
  };

  const status = statusMap[document.status || ''] || 'draft';

  return {
    id: document.documentUUID || document.id || '',
    number: document.documentNumber || 'N/A',
    status: status,
    senderName: document.senderName || 'N/A',
    senderAddress: document.originAddress || '',
    senderCountry: '', // Not available in DocumentDto
    consigneeName: document.receiverName || 'N/A',
    consigneeAddress: document.destinationAddress || '',
    consigneeCountry: '', // Not available in DocumentDto
    companyName: '', // Not available in DocumentDto
    companyAddress: '', // Not available in DocumentDto
    companyCountry: '', // Not available in DocumentDto
    goodsDescription: document.cargoDescription || '',
    grossWeight: document.cargoWeight || 0,
    volume: undefined,
    packageCount: parseInt(document.cargoPackaging || '0', 10),
    placeOfTakeover: document.originAddress || '',
    dateOfTakeover: document.created || new Date().toISOString(),
    placeOfDelivery: document.destinationAddress || '',
    dateOfDelivery: document.actualDeliveryDate || undefined,
    signatures: [],
    deliveryNotes: [],
    companyId: document.companyId?.toString() || '',
    createdAt: document.created || new Date().toISOString(),
    updatedAt: document.created || new Date().toISOString(),
  };
}

export default function ECMRList() {
  const navigate = useNavigate();
  const { user, isSystemAdmin } = useKeycloak();
  const { toast } = useToast();
  const [documents, setDocuments] = useState<ECMR[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const pageSize = 10;

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getDocuments({
        page: currentPage,
        pageSize: pageSize,
        searchKeyword: searchKeyword || undefined,
      });

      let filteredDocs = response.items || [];

      // Apply status filter on frontend (backend doesn't support status filter yet)
      if (statusFilter !== 'all') {
        const statusMap: Record<string, string> = {
          'draft': 'Draft',
          'pending': 'Pending',
          'in_transit': 'InTransit',
          'delivered': 'Delivered',
          'cancelled': 'Cancelled',
        };
        const backendStatus = statusMap[statusFilter];
        if (backendStatus) {
          filteredDocs = filteredDocs.filter(doc => {
            if (!doc.status) return false;
            const docStatus = typeof doc.status === 'string' 
              ? doc.status 
              : String(doc.status);
            return docStatus === backendStatus;
          });
        }
      }

      const ecmrs = filteredDocs.map(mapDocumentDtoToECMR);
      setDocuments(ecmrs);
      setTotalPages(response.metadata?.totalPages || 1);
      setTotalCount(response.metadata?.totalCount || 0);
    } catch (err) {
      const errorToast = getErrorToast(err, 'Erro', 'Erro ao carregar eCMRs');
      setError(errorToast.description);
      toast({
        title: errorToast.title,
        description: errorToast.description,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadDocuments();
  };

  const columns: Column<ECMR>[] = [
    {
      key: 'number',
      header: 'Número',
      render: (ecmr) => (
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <FileText className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="font-medium font-mono">{ecmr.number}</p>
            {ecmr.companyName && (
              <p className="text-sm text-muted-foreground">{ecmr.companyName}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'route',
      header: 'Rota',
      render: (ecmr) => (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="h-3 w-3 text-status-pending" />
            <span>{ecmr.placeOfTakeover}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-3 w-3 text-status-delivered" />
            <span>{ecmr.placeOfDelivery}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'goods',
      header: 'Mercadoria',
      render: (ecmr) => (
        <div className="max-w-[200px]">
          <p className="truncate">{ecmr.goodsDescription}</p>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Package className="h-3 w-3" />
            <span>{ecmr.packageCount} volumes • {ecmr.grossWeight} kg</span>
          </div>
        </div>
      ),
    },
    {
      key: 'date',
      header: 'Data',
      render: (ecmr) => (
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <span>
            {ecmr.dateOfTakeover 
              ? new Date(ecmr.dateOfTakeover).toLocaleDateString('pt-PT')
              : 'N/A'}
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      render: (ecmr) => <StatusBadge status={ecmr.status} />,
    },
    {
      key: 'actions',
      header: '',
      render: (ecmr) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/ecmrs/${ecmr.id}`);
          }}
        >
          Ver Detalhes
        </Button>
      ),
      className: 'text-right',
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="eCMRs"
        description={isSystemAdmin ? 'Todos os documentos eCMR do sistema' : `eCMRs de ${user?.companyName || 'sua empresa'}`}
        actions={
          <Button onClick={() => navigate('/ecmrs/new')}>
            <Plus className="h-4 w-4 mr-2" />
            Novo eCMR
          </Button>
        }
      />

      {error && (
        <div className="p-4 rounded-lg bg-destructive/10 text-destructive text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSearch} className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <Input
          placeholder="Pesquisar eCMRs..."
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          className="max-w-sm"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filtrar por estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os estados</SelectItem>
            <SelectItem value="draft">Rascunho</SelectItem>
            <SelectItem value="pending">Pendente</SelectItem>
            <SelectItem value="in_transit">Em Trânsito</SelectItem>
            <SelectItem value="delivered">Entregue</SelectItem>
            <SelectItem value="cancelled">Cancelado</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" variant="outline">
          Pesquisar
        </Button>
      </form>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={documents}
            onRowClick={(ecmr) => navigate(`/ecmrs/${ecmr.id}`)}
            emptyMessage="Nenhum eCMR encontrado"
          />
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            itemsShown={documents.length}
            itemLabel="eCMRs"
            onPageChange={setCurrentPage}
          />
        </>
      )}
    </div>
  );
}
